import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../services/supabase.ts';
import { propertyService } from '../../services/propertyService.ts';
import { BillStatus } from '../../types/payment.ts';
import { Property } from '../../types/property.ts';
import { Tenant } from '../../types/tenant.ts';
import { formatCurrency, formatDate } from '../../utils/formatters.ts';
import { formatMonthYear } from '../../utils/dateUtils.ts';

interface PaymentFormProps {
  initialData?: {
    tenantId?: string;
    billId?: string;
    amount?: number;
  } | null;
  onClose: () => void;
  onRefetch: () => Promise<void>;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ initialData, onClose, onRefetch }) => {
  const [loading, setLoading] = useState(false);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [pendingBills, setPendingBills] = useState<any[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<string>('');
  const [selectedProperty, setSelectedProperty] = useState<string>('');
  const [selectedBill, setSelectedBill] = useState<string>('');
  const [formData, setFormData] = useState<{
    total_amount: number;
    gst_amount: number;
    payment_date: string;
    payment_mode: 'Cash';
    payment_to: 'Ahamad';
    notes: string;
  }>({
    total_amount: 0,
    gst_amount: 0,
    payment_date: new Date().toISOString().split('T')[0],
    payment_mode: 'Cash',
    payment_to: 'Ahamad',
    notes: ''
  });
  const [formEnabled, setFormEnabled] = useState(false);
  const [maxAllowedAmount, setMaxAllowedAmount] = useState<number>(0);

  // Add validation message states
  const [validationMessages, setValidationMessages] = useState({
    tenant: '',
    property: '',
    bill: '',
    amount: '',
    paymentMode: '',
    paymentTo: '',
    paymentDate: ''
  });

  useEffect(() => {
    fetchTenants();
    fetchProperties();
  }, []);

  const fetchPendingBills = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('bills')
        .select('*')
        .eq('tenant_id', selectedTenant)
        .eq('property_id', selectedProperty)
        .in('status', ['Unpaid', 'Partial']) // Include both Unpaid and Partial bills
        .order('due_date', { ascending: true });

      if (data) {
        setPendingBills(data);
        if (data.length > 0) {
          let billToSelect = null;

          // Prioritize initialData.billId
          if (initialData?.billId) {
            const initialBill = data.find(bill => bill.id === initialData.billId);
            if (initialBill) {
              billToSelect = initialBill;
            }
          }

          // If not found via initialData.billId, try matching by payment_date
          if (!billToSelect && formData.payment_date) {
            const paymentDateFormatted = formatMonthYear(formData.payment_date);
            const matchingBill = data.find(bill => formatMonthYear(bill.month) === paymentDateFormatted);
            if (matchingBill) {
              billToSelect = matchingBill;
            }
          }

          // If still no bill selected, default to the first bill
          if (!billToSelect) {
            billToSelect = data[0];
          }

          setSelectedBill(billToSelect.id);
          const remainingAmount = billToSelect.amount - (billToSelect.paid_amount || 0);
          // Only update total_amount if initialData.amount is not provided,
          // as initialData.amount should take precedence if available (handled in another useEffect)
          if (initialData?.amount === undefined) {
            setFormData(prev => ({
              ...prev,
              total_amount: remainingAmount
            }));
          }
          setMaxAllowedAmount(remainingAmount);
        } else {
          // No pending bills, reset selection and amount
          setSelectedBill('');
           if (initialData?.amount === undefined) {
            setFormData(prev => ({ ...prev, total_amount: 0 }));
          }
          setMaxAllowedAmount(0);
        }
      }
    } catch (error) {
      console.error('Error fetching pending bills:', error);
    }
  }, [selectedTenant, selectedProperty, formData.payment_date, initialData]);

  useEffect(() => {
    if (selectedTenant && selectedProperty) {
      fetchPendingBills();
    }
  }, [selectedTenant, selectedProperty, fetchPendingBills]);

  // Removed the useEffect that previously defaulted selectedBill based on pendingBills only.
  // The logic is now integrated into fetchPendingBills's useEffect.

  useEffect(() => {
    if (initialData?.tenantId) {
      setSelectedTenant(initialData.tenantId);
      const tenant = tenants.find(t => t.id === initialData.tenantId);
      if (tenant) {
        setSelectedProperty(tenant.property_id);
        
        if (initialData.billId) {
          setSelectedBill(initialData.billId);
        }
        
        if (initialData.amount) {
          setFormData(prev => ({
            ...prev,
            total_amount: initialData.amount || 0 // Ensure we always set a number
          }));
        }
      }
    }
  }, [initialData, tenants]);

  useEffect(() => {
    // Enable form only when a tenant is selected
    setFormEnabled(!!selectedTenant);
  }, [selectedTenant]);

  useEffect(() => {
    if (selectedBill) {
      const selectedBillData = pendingBills.find(b => b.id === selectedBill);
      if (selectedBillData) {
        const remainingAmount = selectedBillData.amount - (selectedBillData.paid_amount || 0);
        setMaxAllowedAmount(remainingAmount);
      }
    }
  }, [selectedBill, pendingBills]);

  // Add ESC key handler
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const fetchTenants = async () => {
    const { data } = await supabase
      .from('tenants')
      .select('*')
      .eq('status', 'Active');
    if (data) setTenants(data);
  };

  const fetchProperties = async () => {
    try {
      const data = await propertyService.fetchRentedProperties();
      setProperties(data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };

  const validateField = (name: string, value: any) => {
    if (!value || (typeof value === 'string' && !value.trim())) {
      setValidationMessages(prev => ({
        ...prev,
        [name]: 'This field is required'
      }));
      return false;
    }
    setValidationMessages(prev => ({
      ...prev,
      [name]: ''
    }));
    return true;
  };

  const validateForm = () => {
    const isValid = {
      tenant: validateField('tenant', selectedTenant),
      property: validateField('property', selectedProperty),
      bill: validateField('bill', selectedBill),
      amount: validateField('amount', formData.total_amount),
      paymentMode: validateField('paymentMode', formData.payment_mode),
      paymentTo: validateField('paymentTo', formData.payment_to),
      paymentDate: validateField('paymentDate', formData.payment_date)
    };

    return Object.values(isValid).every(valid => valid);
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers and empty string
    if (value === '' || /^\d*$/.test(value)) {
      const numValue = parseInt(value) || 0;
      const limitedValue = Math.min(numValue, maxAllowedAmount);
      validateField('amount', limitedValue);
      setFormData({
        ...formData,
        total_amount: limitedValue
      });
    }
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    if (text.length <= 50) {
      setFormData({ ...formData, notes: text });
    }
  };

  const handleBillChange = (billId: string) => {
    const selectedBillData = pendingBills.find(b => b.id === billId);
    if (selectedBillData) {
      const remainingAmount = selectedBillData.amount - (selectedBillData.paid_amount || 0);
      setSelectedBill(billId);
      setFormData(prev => ({
        ...prev,
        total_amount: remainingAmount
      }));
      setMaxAllowedAmount(remainingAmount);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Get current bill details
      const { data: bill } = await supabase
        .from('bills')
        .select('amount, paid_amount, status')
        .eq('id', selectedBill)
        .single();

      if (!bill) throw new Error('Bill not found');

      const currentPaidAmount = bill.paid_amount || 0;
      const newPaidAmount = currentPaidAmount + formData.total_amount;
      let billStatus: BillStatus;

      if (newPaidAmount >= bill.amount) {
        billStatus = 'Paid';
      } else if (newPaidAmount > 0) {
        billStatus = 'Partial';
      } else {
        billStatus = 'Unpaid';
      }

      // First create payment record
      const { error: paymentError } = await supabase
        .from('payments')
        .insert([{
          ...formData,
          bill_id: selectedBill,
          tenant_id: selectedTenant,
          property_id: selectedProperty
        }])
        .select()
        .single();

      if (paymentError) throw paymentError;

      // Then update bill status and paid amount
      const { error: billError } = await supabase
        .from('bills')
        .update({
          status: billStatus,
          paid_amount: newPaidAmount,
          payment_date: billStatus === 'Paid' ? new Date().toISOString().split('T')[0] : null
        })
        .eq('id', selectedBill);

      if (billError) throw billError;

      await onRefetch(); // Refetch data silently
      onClose();
    } catch (error: any) {
      console.error('Error submitting payment:', error);
      alert(error.message || 'Failed to submit payment');
    } finally {
      setLoading(false);
    }
  };

  const renderBillOption = (bill: any) => {
    const paidAmount = bill.paid_amount || 0;
    const remainingAmount = bill.amount - paidAmount;
    const paidStatus = paidAmount > 0
      ? ` (Paid: ${formatCurrency(paidAmount)} | Remaining: ${formatCurrency(remainingAmount)})`
      : ` (${formatCurrency(bill.amount)})`;

    return `${formatMonthYear(bill.month)} ${paidStatus}`;
  };

  // Add handler to prevent scroll propagation
  const handleModalWheel = (e: React.WheelEvent) => {
    e.stopPropagation();
  };

  return (
    <div className="modal" onClick={onClose}>
      <div 
        className="payment-form"
        onClick={(e) => e.stopPropagation()}
        onWheel={handleModalWheel}
      >
        <div className="modal-header">
          <h2>Record Payment</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit} noValidate> {/* Add noValidate here */}
          <div className="form-group">
            <label>Tenant *</label>
            <select
              value={selectedTenant}
              onChange={(e) => {
                validateField('tenant', e.target.value);
                const tenant = tenants.find(t => t.id === e.target.value);
                setSelectedTenant(e.target.value);
                if (tenant) {
                  setSelectedProperty(tenant.property_id);
                  setSelectedBill('');
                } else {
                  // Reset form data when no tenant is selected
                  setSelectedProperty('');
                  setSelectedBill('');
                  setFormData(prev => ({
                    ...prev,
                    total_amount: 0
                  }));
                }
              }}
              // Remove required attribute
            >
              <option value="">Select Tenant</option>
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.full_name}
                </option>
              ))}
            </select>
            {validationMessages.tenant && (
              <span className="validation-message">{validationMessages.tenant}</span>
            )}
          </div>

          <div className="form-group">
            <label>Property *</label>
            <select
              value={selectedProperty}
              onChange={(e) => {
                validateField('property', e.target.value);
                setSelectedProperty(e.target.value);
              }}
              disabled={!formEnabled}
            >
              <option value="">Select Property</option>
              {properties
                .filter(property => property.id === selectedProperty)
                .map((property) => (
                  <option key={property.id} value={property.id}>
                    {property.name}
                  </option>
                ))}
            </select>
            {validationMessages.property && (
              <span className="validation-message">{validationMessages.property}</span>
            )}
          </div>

          <div className="form-group">
            <label>Bill *</label>
            <select
              value={selectedBill}
              onChange={(e) => handleBillChange(e.target.value)}
              disabled={!formEnabled}
            >
              <option value="">Select Bill</option>
              {pendingBills.map((bill) => (
                <option key={bill.id} value={bill.id}>
                  {renderBillOption(bill)}
                </option>
              ))}
            </select>
            {selectedBill && (
              <div className="bill-payment-details">
                <p>Total Bill Amount: {formatCurrency(pendingBills.find(b => b.id === selectedBill)?.amount || 0)}</p>
                <p>Already Paid: {formatCurrency(pendingBills.find(b => b.id === selectedBill)?.paid_amount || 0)}</p>
                <p>Remaining: {formatCurrency(maxAllowedAmount)}</p>
              </div>
            )}
            {validationMessages.bill && (
              <span className="validation-message">{validationMessages.bill}</span>
            )}
          </div>

          <div className="form-group">
            <label>Bill Details</label>
          </div>

          <div className="form-group">
            <label>Amount *</label>
            <input
              type="text"
              value={formData.total_amount}
              onChange={handleAmountChange}
              placeholder="Enter amount"
              maxLength={7} // To prevent typing more than 7 digits
              disabled={!formEnabled}
            />
            {formEnabled && maxAllowedAmount > 0 && (
              <span className="form-hint">
                Maximum allowed: ₹{maxAllowedAmount.toLocaleString()}
              </span>
            )}
            {validationMessages.amount && (
              <span className="validation-message">{validationMessages.amount}</span>
            )}
          </div>

          <div className="form-group">
            <label>Payment Mode</label>
            <select
              value={formData.payment_mode}
              onChange={(e) => {
                validateField('paymentMode', e.target.value);
                setFormData({
                  ...formData,
                  payment_mode: e.target.value as any
                });
              }}
              disabled={!formEnabled}
            >
              <option value="Cash">Cash</option>
              <option value="Online">Online</option>
              <option value="Other">Other</option>
            </select>
            {validationMessages.paymentMode && (
              <span className="validation-message">{validationMessages.paymentMode}</span>
            )}
          </div>

          <div className="form-group">
            <label>Collected By</label>
            <select
              value={formData.payment_to}
              onChange={(e) => {
                validateField('paymentTo', e.target.value);
                setFormData({
                  ...formData,
                  payment_to: e.target.value as any
                });
              }}
              disabled={!formEnabled}
            >
              <option value="Ahamad">Ahamad</option>
              <option value="Rehan">Rehan</option>
              <option value="Irfan">Irfan</option>
              <option value="Salman">Salman</option>
              <option value="Asad">Asad</option>
              <option value="Other">Other</option>
            </select>
            {validationMessages.paymentTo && (
              <span className="validation-message">{validationMessages.paymentTo}</span>
            )}
          </div>

          <div className="form-group">
            <label>Payment Date *</label>
            <input
              type="date"
              value={formData.payment_date}
              onChange={(e) => {
                // Validate year is not more than 4 digits
                const date = new Date(e.target.value);
                const year = date.getFullYear();
                if (year > 9999) {
                  return; // Don't update if year is more than 4 digits
                }
                validateField('paymentDate', e.target.value);
                setFormData({
                  ...formData,
                  payment_date: e.target.value
                });
              }}
              max="9999-12-31" // Add max date attribute
              disabled={!formEnabled}
            />
            {validationMessages.paymentDate && (
              <span className="validation-message">{validationMessages.paymentDate}</span>
            )}
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={formData.notes}
              onChange={handleNotesChange}
              disabled={!formEnabled}
            />
            <span className="form-hint">
              {`${formData.notes.length} of 50 characters`}
            </span>
          </div>

          <div className="form-actions">
            <button 
              type="submit" 
              className="btn-primary" 
              disabled={loading || !formEnabled}
            >
              {loading ? 'Recording...' : 'Record Payment'}
            </button>
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentForm;
